/**
 * Firebase Configuration
 *
 * This module initializes only the essential Firebase services (app, auth, firestore, storage)
 * that are required for initial app load. Optional services (analytics, messaging, functions)
 * are lazy-loaded by their respective service modules to reduce initial bundle size.
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  memoryLocalCache,
  Firestore,
} from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { env } from './env';

let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;

// Track Firebase initialization state for diagnostics
export type FirebaseInitState = 'pending' | 'success' | 'degraded' | 'failed';
let firebaseInitState: FirebaseInitState = 'pending';

/**
 * Get the current Firebase initialization state
 */
export function getFirebaseInitState(): FirebaseInitState {
  return firebaseInitState;
}

try {
  const firebaseConfig = JSON.parse(env.firebaseConfig);

  if (!firebaseConfig.apiKey) {
    throw new Error('Firebase API key not configured');
  }

  app = initializeApp(firebaseConfig);
  auth = getAuth(app);

  // Initialize Firestore with persistent local cache (IndexedDB)
  // This enables offline support and faster loads from cache
  // Falls back to memory cache if IndexedDB fails (Safari Private Mode, corrupted DB)
  try {
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
    firebaseInitState = 'success';
  } catch (firestoreError) {
    console.warn(
      '[Firebase] Persistent cache failed, falling back to memory cache:',
      firestoreError
    );
    // Fallback to memory-only cache - app works but without offline persistence
    db = initializeFirestore(app, {
      localCache: memoryLocalCache(),
    });
    firebaseInitState = 'degraded';
  }

  storage = getStorage(app);

  // NOTE: Analytics, Messaging, and Functions are lazy-loaded by their
  // respective service modules to reduce initial bundle size:
  // - Analytics: src/services/analytics.ts
  // - Messaging: getMessagingInstance() below
  // - Functions: src/services/admin.ts, src/services/moderation.ts
} catch (error) {
  console.error('Firebase initialization error:', error);
  console.warn('Please configure Firebase in .env.local file');
  firebaseInitState = 'failed';
  // Create placeholder values to prevent import errors
  app = {} as FirebaseApp;
  auth = {} as Auth;
  db = {} as Firestore;
  storage = {} as FirebaseStorage;
}

export { app, auth, db, storage };
export const appId = env.appId;

// ============================================
// LAZY-LOADED MESSAGING
// ============================================

// Cached messaging instance
let messagingInstance: import('firebase/messaging').Messaging | null = null;
let messagingModule: typeof import('firebase/messaging') | null = null;

/**
 * Get the messaging instance asynchronously (lazy-loads the module)
 * Returns null if messaging is not supported
 */
export async function getMessagingInstance(): Promise<
  import('firebase/messaging').Messaging | null
> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }

  // Lazy-load the messaging module
  if (!messagingModule) {
    messagingModule = await import('firebase/messaging');
  }

  const supported = await messagingModule.isSupported();
  if (!supported) {
    return null;
  }

  // If messaging was already initialized, return it
  if (messagingInstance) {
    return messagingInstance;
  }

  // Otherwise initialize and return
  try {
    messagingInstance = messagingModule.getMessaging(app);
    return messagingInstance;
  } catch (error) {
    console.error('Failed to initialize Firebase Messaging:', error);
    return null;
  }
}
